import React, { useState, useEffect } from 'react';
import styles from './AttributeModal.module.css';

const AttributeModal = ({ isOpen, onClose, onSubmit, isLoading, initialData, mode = 'create' }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'text',
    category: 'specification',
    description: '',
    options: [] // NUEVO: Para variantes con múltiples criterios
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData && mode === 'edit') {
      setFormData({
        name: initialData.name || '',
        type: initialData.type || 'text',
        category: initialData.category || 'specification',
        description: initialData.description || '',
        options: initialData.options || [] // NUEVO
      });
    } else {
      setFormData({
        name: '',
        type: 'text',
        category: 'specification',
        description: '',
        options: [] // NUEVO
      });
    }
    setErrors({});
  }, [isOpen, initialData, mode]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    if (!formData.type) {
      newErrors.type = 'El tipo es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpiar error del campo cuando el usuario empieza a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // NUEVO: Manejar adición de opciones para variantes
  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, { value: '', display_name: '', color_hex: '' }]
    }));
  };

  // NUEVO: Manejar cambio en una opción
  const updateOption = (index, field, value) => {
    setFormData(prev => {
      const newOptions = [...prev.options];
      newOptions[index] = { ...newOptions[index], [field]: value };
      return { ...prev, options: newOptions };
    });
  };

  // NUEVO: Eliminar una opción
  const removeOption = (index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{mode === 'create' ? 'Crear Atributo' : 'Editar Atributo'}</h2>
          <button className={styles.closeButton} onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="name">Nombre del Atributo</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ej: Color, Talla, Material"
              className={errors.name ? styles.inputError : ''}
            />
            {errors.name && <span className={styles.errorMessage}>{errors.name}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="type">Tipo de Atributo</label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              className={errors.type ? styles.inputError : ''}
            >
              <option value="text">Texto</option>
              <option value="color">Color</option>
              <option value="number">Número</option>
              <option value="boolean">Sí/No</option>
            </select>
            {errors.type && <span className={styles.errorMessage}>{errors.type}</span>}
            <small className={styles.helpText}>
              Elige el tipo según cómo quieras que se visualice este atributo
            </small>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="category">Categoría de Atributo</label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
            >
              <option value="specification">Especificación (Solo información)</option>
              <option value="variant">Variante (Cliente elige)</option>
            </select>
            <small className={styles.helpText}>
              <strong>Especificación:</strong> Solo se muestra en detalles (WiFi, Material)<br/>
              <strong>Variante:</strong> El cliente elige y crea versiones del producto (Color, Talla)
            </small>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="description">Descripción (Opcional)</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Ej: Selecciona el color del producto"
              rows="3"
            />
            <small className={styles.helpText}>
              Ayuda adicional para el cliente
            </small>
          </div>

          {/* NUEVO: Opciones para variantes */}
          {formData.category === 'variant' && (
            <div className={styles.formGroup}>
              <label>Opciones disponibles (Ej: Negro, Blanco, Rojo)</label>
              <small className={styles.helpText}>
                Agrega los valores que puede tener este atributo
              </small>
              
              {formData.options.map((option, index) => (
                <div key={index} className={styles.optionGroup}>
                  <input
                    type="text"
                    placeholder="Valor (ej: Negro)"
                    value={option.value}
                    onChange={(e) => updateOption(index, 'value', e.target.value)}
                    className={styles.optionInput}
                  />
                  
                  {formData.type === 'color' && (
                    <input
                      type="color"
                      placeholder="Color hex"
                      value={option.color_hex || '#000000'}
                      onChange={(e) => updateOption(index, 'color_hex', e.target.value)}
                      className={styles.colorInput}
                    />
                  )}
                  
                  <button
                    type="button"
                    onClick={() => removeOption(index)}
                    className={styles.removeOptionBtn}
                  >
                    ✕
                  </button>
                </div>
              ))}
              
              <button
                type="button"
                onClick={addOption}
                className={styles.addOptionBtn}
              >
                + Agregar opción
              </button>
            </div>
          )}

          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isLoading}
            >
              {isLoading ? 'Guardando...' : (mode === 'create' ? 'Crear' : 'Guardar Cambios')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AttributeModal;
